/*
* Point-of-entry for getting & setting values from local storage.
* Most of these values retrieved act like FV level 'settings'.
* Initially I thought we might want to to cache the actual values on here as well,
* but with all current use cases that's unnecessary at this time.
* ( Rather than store them on FV.settings, the thought was that keeping logic separate would facilitate
* an easier refactor and provide clarity by keeping the properties in the same location).
* Perhaps future use cases may have me rethink this.
* Writing cached properties make it easier to discern defaults and possible values --
* but for now this is accomplished this through comments.
* Unset items are treated with 'default' values.
* Setting defaults here may prove clearer than having them set throughout the codebase -- see below comments.
* Note that getters may not return exactly what exists in localStorage but the value expected by the app --
* essentially what would be the proposed instance variables values on here.
* For now not an instantiable class to mimic old FV.settings & for simplicity of initial refactor.
* Everything now persisted in browser localStorage; with all properties now referenced inside here,
* could switch to another method or persistence.
* */

var MostUsedReasonCodesComparator =
    require('./reasonCodes/shapeAvailableReasonCodes').prototype.mostUsedCodeCountComparator;


module.exports = {

    LEFT_PANE_WIDTH: 'FV_LEFT_W', // defaults set is sidePanel models from css -- may want to set here via an initialize.
    RIGHT_PANE_WIDTH: 'FV_RIGHT_W',
    LAST_DB: 'FV_DB',
    SHOW_OVERLAYS: 'FV_SHOW_OVERLAY', // 0/1: default is 1 ( DVBC-1754)
    SHOW_GREENBAR: 'FV_SHOW_GREENBAR', // 0/1: default is 0
    LAST_REDACTION_FILL: 'lastRedactionFill', // default defined redaction model -- WHITE_BLACK_BORDER
    LAST_REASON_CODES: 'lastReasonCodes', // default: unset; equivalent to 'null' (value applied when user sets to none)
    PG_MODE_APPLY_ALL_REDACTIONS: 'pgModeApplyAllRedactions', // 0/1: default is 0

    LAST_GRAPHIC_NOTE_SHAPE_COLOR: 'FV-lastGraphicNoteShapeColor', // hex color string, eg: '#a2cdb3'
    LAST_GRAPHIC_NOTE_SHAPE_LINE_WIDTH: 'FV-lastGraphicNoteShapeLineWidth', // 1, 2, 3, 4, or 5 -- see dropdown for available values
    LAST_GRAPHIC_NOTE_SHAPE: 'FV-lastGraphicNoteShape', // default is free from graphic note model's default shape type

    LAST_NOTE_SORT_TYPE: 'FV-lastNoteSort', // see notes.models.comparators: ctime, cusr, type, pageNumber; default: ctime
    LAST_NOTE_SORT_DIRECTION: 'FV-lastNoteSortDirection',  // 'asc' or 'desc'; default: 'sortDir'; see notes.state.sortState

    APPLIED_REASON_CODE_COUNTS: 'FV-appliedReasonCodeCounts',


    /* Now not storing any cached property default values on here -- if that changes,
     * would want to re-implement and set them here based on the values initially in local storage,
     * perhaps through an initialize function like this.
     * */
    //initialize: function() {},

    /*
    * Generic function: proxy window.setItem to local storage
    * Update provided key with given value.
    * */
    setItem: function(key, value) {
        localStorage.setItem(key, value);
    },
    /*
    * Note: spec returns null if item doesn't exist.
    * */
    getItem: function( key ) {
        return localStorage.getItem(key);
    },

    getLastDb: function() {
        return this.getItem( this.LAST_DB );
    },
    setLastDb: function( value ) {
        return this.setItem( this.LAST_DB, value );
    },

    /* Note --  Handle conversion between string (in storage) and expected numeric value */
    getShowOverlays: function() {

        var showOverlaysLocalStorageValue = this.getItem( this.SHOW_OVERLAYS );

        if (!showOverlaysLocalStorageValue)
            return 1;

        return parseInt(showOverlaysLocalStorageValue);

    },
    // Could potentially utilize a toggle fn -- see toggleApplyAllRedactionsInPageMode ...
    // though now not necessary
    setShowOverlays: function( value ) {
        return this.setItem( this.SHOW_OVERLAYS, value );
    },

    getShowGreenbar: function() {
        var showGreenbar = this.getItem( this.SHOW_GREENBAR );
        return showGreenbar && parseInt(showGreenbar) ? 1 : 0;
    },
    setShowGreenbar: function( value ) {
        return this.setItem( this.SHOW_GREENBAR, value );
    },

    getLastRedactionFill: function() {
        return this.getItem( this.LAST_REDACTION_FILL );
    },
    setLastRedactionFill: function( value ) {
        this.setItem( this.LAST_REDACTION_FILL, value );
    },

    // Note: string to array.
    getLastReasonCodes: function() {
        return JSON.parse( this.getItem( this.LAST_REASON_CODES ) );
    },

    /*
     * Store stringified array of codes if any provided,
     * otherwise store null instead of an empty array (for clarity)
     * */
    setLastReasonCodes: function( aCodes ) {

        var codesToStore = aCodes && aCodes.length > 0
            ? JSON.stringify( aCodes )
            : null;

        this.setItem( this.LAST_REASON_CODES, codesToStore );
    },


    getAppliedReasonCodeCounts: function() {

        return JSON.parse( this.getItem( this.APPLIED_REASON_CODE_COUNTS ) );

    },

    /**
     *  Stores array of { code: count } sorted by count.
     *  Avoiding use of object with keys: technically it would have not no order
     *  Could be more performent to store an array in index order of most used,
     *  but this leaves more flexibility in case we ever want to know the counts.
     *  Can't impose a max number to store in the array -- say max of 5 and a new code added,
     *  the new code would only have a count of 1 would be cut off prior to saving;
     *  the next time added it would then have a count of 1 ... it would never make the cut.
     *  -- could now move to keys/greater if not sorting.
     */
    setAppliedReasonCodeCounts: function( aCodes ) {

        if ( !aCodes || aCodes.length < 1 )
            return;

        var aReasonCodeCounts = this.getAppliedReasonCodeCounts();

        if ( aReasonCodeCounts === null ) {
            aReasonCodeCounts = [];
        }

        for (var c=0; c<aCodes.length; c++ ) {

            var code = aCodes[c];

            var existingCodeCount = _.findWhere( aReasonCodeCounts, { code: code });

            if ( existingCodeCount ) {

                existingCodeCount.count += 1;

            } else {

                aReasonCodeCounts.push( {
                    code: code,
                    count: 1
                } );

            }
        }

        // Sort for ease in debug and so we don't need to call multiple times in comparator fn's to get the top amount.
        // Also gives consistency in knowing which come first if the have the same count in the top.
        // Sorting on way in means no need to do so multiple times on the way out.
        aReasonCodeCounts.sort( MostUsedReasonCodesComparator );

        this.setItem( this.APPLIED_REASON_CODE_COUNTS, JSON.stringify(aReasonCodeCounts) );

    },

    getPgModeApplyAllRedactions: function() {
        var applyRedactions = this.getItem( this.PG_MODE_APPLY_ALL_REDACTIONS );
        return applyRedactions && parseInt(applyRedactions) ? 1 : 0;
    },
    setPgModeApplyAllRedactions: function( value ) {
        this.setItem( this.PG_MODE_APPLY_ALL_REDACTIONS, value );
    },
    toggleApplyAllRedactionsInPageMode: function() {
        this.setPgModeApplyAllRedactions( this.getPgModeApplyAllRedactions() ? 0 : 1 );
    },

    getLastGraphicNoteShapeColor: function() {
        return this.getItem( this.LAST_GRAPHIC_NOTE_SHAPE_COLOR );
    },
    setLastGraphicNoteShapeColor: function(hexValue) {
        this.setItem( this.LAST_GRAPHIC_NOTE_SHAPE_COLOR, hexValue );
    },

    getLastGraphicNoteShapeLineWidth: function() {
        return this.getItem( this.LAST_GRAPHIC_NOTE_SHAPE_LINE_WIDTH );
    },

    setLastGraphicNoteShapeLineWidth: function( width ) {
        this.setItem( this.LAST_GRAPHIC_NOTE_SHAPE_LINE_WIDTH, width );
    },

    getLastGraphicNoteShape: function() {
        return this.getItem( this.LAST_GRAPHIC_NOTE_SHAPE );
    },

    setLastGraphicNoteShape: function( shapeType ) {
        this.setItem( this.LAST_GRAPHIC_NOTE_SHAPE, shapeType );
    },

    getLastNoteSortType: function() {
        return this.getItem( this.LAST_NOTE_SORT_TYPE );
    },
    setLastNoteSortType: function(comparator) {
        this.setItem( this.LAST_NOTE_SORT_TYPE, comparator );
    },

    getLastNoteSortDirection: function() {
        return this.getItem( this.LAST_NOTE_SORT_DIRECTION );
    },

    setLastNoteSortDirection: function(direction) {
        this.setItem( this.LAST_NOTE_SORT_DIRECTION, direction );
    }

};






/*****************
 ** WEBPACK FOOTER
 ** ./assets/js/modules/FV/localStorageSettings.js
 ** module id = 78
 ** module chunks = 0
 **/